import { useEffect, useState } from 'react'
import { supabase, readableError } from '../lib/supabase'
import {
  DIMENSIONS, WARMTH_BELOW, averageOf, chipsFor, tierLabel,
} from '../lib/encourage'

const START = { overall: 55, lyrics: 55, theology: 55, fourth: 55 }

/**
 * Give feedback on someone else's song.
 *
 * Three steps: drag four lines, pick any comments that fit, done.
 *
 * The rater does NOT choose whether they are a listener or church staff. The
 * prototype shows a General / Verified pastor switch, but that is a demo
 * affordance — here the answer comes from my_rater_kind(), which reads a
 * verified, approved church_members row and nothing else. The database rejects
 * an insert whose rater_kind disagrees with it, so this component cannot lie
 * even if it wanted to.
 *
 * A written note is a further grant again (can_leave_freetext), and is
 * moderated before the writer ever sees it.
 */
export default function Encourage({ song, onSent }) {
  const [kind, setKind] = useState(null)      // 'listener' | 'pastor'
  const [canWrite, setCanWrite] = useState(false)
  const [step, setStep] = useState(1)
  const [val, setVal] = useState(START)
  const [chips, setChips] = useState([])
  const [picked, setPicked] = useState([])
  const [note, setNote] = useState('')
  const [ratingId, setRatingId] = useState(null)
  const [busy, setBusy] = useState(false)
  const [warm, setWarm] = useState(false)
  const [err, setErr] = useState(null)

  // Ask the database what this person is. Never asked of the client.
  useEffect(() => {
    let alive = true
    Promise.all([
      supabase.rpc('my_rater_kind'),
      supabase.rpc('may_leave_freetext'),
    ]).then(([k, f]) => {
      if (!alive) return
      setKind(k.data === 'pastor' ? 'pastor' : 'listener')
      setCanWrite(f.data === true)
    })
    return () => { alive = false }
  }, [])

  const submit = async () => {
    setBusy(true); setErr(null)
    try {
      const { data, error } = await supabase.from('song_ratings')
        .upsert({
          song_id: song.id,
          user_id: (await supabase.auth.getUser()).data.user.id,
          overall: val.overall, lyrics: val.lyrics,
          theology: val.theology, fourth: val.fourth,
        }, { onConflict: 'song_id,user_id' })
        .select().maybeSingle()
      if (error) throw error
      setRatingId(data?.id ?? null)

      // A low rating is recorded, thanked, and stops there. It never becomes
      // a set of critical sentences pointed at the writer.
      if (averageOf(val) < WARMTH_BELOW) { setWarm(true); setStep(3); return }

      setChips(chipsFor(val, kind))
      setStep(2)
    } catch (e) {
      setErr(readableError(e))
    } finally {
      setBusy(false)
    }
  }

  const finish = async () => {
    setBusy(true); setErr(null)
    try {
      if (picked.length && ratingId) {
        await supabase.from('song_rating_comments')
          .insert(picked.map((chip_key) => ({ rating_id: ratingId, chip_key })))
      }
      if (canWrite && note.trim() && ratingId) {
        await supabase.from('song_rating_notes')
          .insert({ rating_id: ratingId, body: note.trim() })
      }
      setStep(3)
      onSent?.()
    } catch (e) {
      setErr(readableError(e))
    } finally {
      setBusy(false)
    }
  }

  if (!kind) return null
  const words = note.trim() ? note.trim().split(/\s+/).length : 0
  const overWords = words > 50

  if (step === 3) {
    return (
      <div className="enc-done">
        <div className="enc-check" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="3" strokeLinecap="round"
               strokeLinejoin="round"><path d="M4 12.5l5 5L20 6.5" /></svg>
        </div>
        <h3 className="enc-h">Feedback sent</h3>
        <p className="small">
          {warm
            ? "People are listening to your song. Thanks for taking the time — what you said goes no further than a private note."
            : `Sent privately to the writer${picked.length ? `, with ${picked.length} comment${picked.length > 1 ? 's' : ''}` : ''}.`}
        </p>
      </div>
    )
  }

  if (step === 2) {
    return (
      <>
        <p className="small" style={{ marginBottom: 12 }}>
          Rating saved. Add any comments that fit — <b>chosen, not typed</b> — or skip.
        </p>
        <div className="enc-chips">
          {chips.map((c) => {
            const on = picked.includes(c.key)
            return (
              <button key={c.key} type="button"
                      className={`enc-chip${on ? ' on' : ''}`}
                      aria-pressed={on}
                      onClick={() => setPicked((p) =>
                        on ? p.filter((k) => k !== c.key) : [...p, c.key])}>
                <span>{c.text}</span><span className="enc-dot" />
              </button>
            )
          })}
        </div>

        {canWrite && (
          <div style={{ marginTop: 16 }}>
            <label className="label">A written word</label>
            <p className="small" style={{ marginTop: -4, marginBottom: 8 }}>
              Specific, kind, useful. Reviewed before the writer sees it.
            </p>
            <textarea className="ta" value={note} rows={3}
                      placeholder="Optional — the rating and comments send either way."
                      onChange={(e) => setNote(e.target.value)} />
            <div className="small" style={{ color: overWords ? 'var(--danger)' : undefined }}>
              {words} / 50 words
            </div>
          </div>
        )}

        <button className="btn" disabled={busy || overWords} onClick={finish}>
          {busy ? 'Sending…' : 'Send feedback'}
        </button>
        <button className="btn btn-quiet" disabled={busy}
                onClick={() => { setPicked([]); setNote(''); finish() }}>
          Skip comments, just send the rating
        </button>
        {err && <p className="err">{err}</p>}
      </>
    )
  }

  return (
    <>
      {kind === 'pastor' && (
        <p className="small" style={{ marginBottom: 12 }}>
          You're rating as verified staff at your church, so you're asked about
          congregational fit and can leave a written word.
        </p>
      )}

      {DIMENSIONS[kind].map(([key, label]) => (
        <div className="slider-row" key={key}>
          <div className="slider-top">
            <span className="lbl">{label}</span>
            <span className="val">{tierLabel(val[key])}</span>
          </div>
          <input type="range" min="0" max="100" value={val[key]}
                 aria-label={label}
                 onChange={(e) => setVal((v) => ({ ...v, [key]: Number(e.target.value) }))} />
        </div>
      ))}

      <p className="small" style={{ textAlign: 'center', margin: '2px 0 12px' }}>
        Drag each line, then send — comments matched to your answers come next.
      </p>
      <button className="btn" disabled={busy} onClick={submit}>
        {busy ? 'Saving…' : 'Submit rating'}
      </button>
      <p className="small" style={{ textAlign: 'center', marginTop: 10 }}>
        Goes privately to the writer. Never shown publicly, never a number.
      </p>
      {err && <p className="err">{err}</p>}
    </>
  )
}
